/** "项目发起" / Creative Brief SOURCE node
 * (VISTOERA_CANVAS_PROJECT_START_CREATIVE_BRIEF_AND_IDEATION_BRANCHING_
 * NODE_V1). Deliberately NOT an AI Provider execution card (spec §3) —
 * this node only ever OWNS/edits a Creative Brief (intent/duration/
 * content goal/assets/constraints) and asks `CanvasBoard` to dispatch
 * one Creative Ideation run; every AI Provider execution happens on the
 * `creative_candidate` nodes it fans out into, never here.
 *
 * §5 "唯一真正应该作为主要输入的是创作意图" drives the whole layout:
 * every OTHER field below the textarea is optional and collapses/stays
 * out of the way, and Generate is never blocked on anything but the
 * textarea being non-empty. */

import {
  DownOutlined,
  LoadingOutlined,
  PlusOutlined,
  RightOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import CanvasSelect from '../CanvasSelect'
import { memo, useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { CREATIVE_IDEATION_CAPABILITY, type ImageAssetPickerSelection } from '../../../../api'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import {
  buildExplicitProviderSelectionPatch,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import { isBriefAffectingPatch } from '../creativeIdeationCanvas'
import { useNodeDefinition } from '../nodeRegistry'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import type { FlowNodeData } from '../types'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'

const NODE_WIDTH = 330

const INTENT_MAX_LENGTH = 800

// spec §6 — quick examples appended (never auto-run) to the intent textarea.
const INTENT_EXAMPLES = [
  '给这个产品想几个创意',
  '做一个搞笑短片',
  '我不知道拍什么，帮我想',
  '突出品质感与情感共鸣',
]

// spec §7 — first-version Content Type list; `null` = "AI判断"（自动）.
// Values are the SAME Chinese labels the AI Harness prompt (
// `creative_ideation_input.py`) renders verbatim into `content_goal` —
// no separate internal enum key, since there is no backend validation
// requiring one (spec §7 "不要硬要求用户选择").
const CONTENT_GOAL_OPTIONS: string[] = [
  '品牌广告',
  '产品上新',
  'IP短剧',
  '知识内容',
  '搞笑娱乐',
  '种草推荐',
  '品牌故事',
  '连续剧',
  '自由创作',
  '其他',
]

// spec §8 — duration chips; "1-3分钟" stores a representative 120s.
const DURATION_CHIPS: Array<{ key: string; label: string; seconds: number | null }> = [
  { key: '15', label: '15秒', seconds: 15 },
  { key: '30', label: '30秒', seconds: 30 },
  { key: '60', label: '60秒', seconds: 60 },
  { key: '120', label: '1-3分钟', seconds: 120 },
]

// spec §45 — no Platform Registry exists yet; short static list, same
// "never hardcode business LOGIC, a plain display list is fine" spirit
// as the Content Type list above.
const PLATFORM_OPTIONS = ['抖音', '小红书', 'YouTube', 'TikTok', 'Instagram', '其他']

const CANDIDATE_COUNT_OPTIONS = [3, 5, 6]

interface AssetRef {
  asset_id: string
  picker_item_id?: string
  asset_type: string
  display_name: string
}

function TagListEditor({
  values,
  placeholder,
  onChange,
}: {
  values: string[]
  placeholder: string
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  const commit = useCallback(() => {
    const v = draft.trim()
    if (!v) return
    if (!values.includes(v)) onChange([...values, v])
    setDraft('')
  }, [draft, onChange, values])
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    }
  }
  return (
    <div className="pcv-brief-section nodrag">
      <div className="pcv-brief-chip-row">
        {values.map((v) => (
          <span key={v} className="pcv-brief-chip is-active">
            {v}
            <CloseOutlined
              style={{ marginLeft: 5, fontSize: 9 }}
              onClick={() => onChange(values.filter((x) => x !== v))}
            />
          </span>
        ))}
      </div>
      <input
        className="pcv-brief-textarea"
        style={{ minHeight: 30, height: 30, padding: '5px 8px' }}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
      />
    </div>
  )
}

function AssetChip({ asset, workspaceId, onRemove }: { asset: AssetRef; workspaceId: string; onRemove: () => void }) {
  const [thumb, setThumb] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    if (asset.picker_item_id && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, asset.picker_item_id, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [asset.picker_item_id, workspaceId])
  return (
    <span className="pcv-brief-asset-chip nodrag" title={asset.display_name}>
      <ImageThumbnailHoverPreview thumbUrl={thumb} />
      <span className="pcv-brief-asset-name">{asset.display_name}</span>
      <button type="button" className="pcv-brief-asset-remove" aria-label="移除" onClick={onRemove}>
        <CloseOutlined />
      </button>
    </span>
  )
}

function ProjectStartNodeImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string; projectId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const workspaceId = nodeData.workspaceId || ''
  const fields = nodeData.fields || {}

  const userIntent = String(fields.userIntent ?? '')
  const contentGoal = (fields.contentGoal as string | null) ?? null
  const targetDurationSeconds = (fields.targetDurationSeconds as number | null) ?? null
  // spec §8/backend `input.schema.json` `duration_source` enum:
  // DEFAULT_UI_VALUE | USER_CONFIRMED_VALUE | AI_SUGGESTED (NOT the bare
  // `USER_CONFIRMED` used elsewhere for `contentGoalSource` — that field
  // has its OWN, differently-named enum on the backend).
  const durationSource = (fields.durationSource as string) || 'DEFAULT_UI_VALUE'
  const assets = Array.isArray(fields.assets) ? (fields.assets as AssetRef[]) : []
  const mustInclude = Array.isArray(fields.mustInclude) ? (fields.mustInclude as string[]) : []
  const mustAvoid = Array.isArray(fields.mustAvoid) ? (fields.mustAvoid as string[]) : []
  const audience = (fields.audience as string) || ''
  const platform = (fields.platform as string | null) ?? null
  const tone = (fields.tone as string) || ''
  const references = Array.isArray(fields.references) ? (fields.references as string[]) : []
  const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | null) ?? null
  const candidateCount = (fields.candidateCount as number) || 3
  const runStatus = (fields.runStatus as string | null) ?? null
  const runErrorMessage = (fields.runErrorMessage as string | null) ?? null

  const [moreOpen, setMoreOpen] = useState(Boolean(fields.moreRequirementsExpanded))
  const [customDurationDraft, setCustomDurationDraft] = useState('')

  const { options: providerOptions, error: providerOptionsError } = useCapabilityProviderOptions(
    workspaceId,
    CREATIVE_IDEATION_CAPABILITY,
    'project_start',
  )
  const resolvedProviderOptionId = useMemo(
    () => resolveCapabilityProviderOptionId({ selectionMode: providerSelectionMode, storedProviderOptionId, options: providerOptions }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )

  const isBusy = runStatus === 'SUBMITTING' || runStatus === 'QUEUED' || runStatus === 'GENERATING'

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
      if (isBriefAffectingPatch(patch)) {
        nodeData.onBriefChanged?.(id)
      }
    },
    [fields, id, nodeData],
  )

  const appendExample = (text: string) => {
    const current = userIntent.trim()
    const next = current ? `${current}\n${text}` : text
    patchFields({ userIntent: next.slice(0, INTENT_MAX_LENGTH) })
  }

  const pickDuration = (chip: (typeof DURATION_CHIPS)[number]) => {
    patchFields({ targetDurationSeconds: chip.seconds, durationSource: 'USER_CONFIRMED_VALUE' })
  }
  const pickAiSuggestedDuration = () => {
    patchFields({ targetDurationSeconds: null, durationSource: 'AI_SUGGESTED' })
  }
  const commitCustomDuration = () => {
    const n = Number(customDurationDraft)
    if (Number.isFinite(n) && n > 0) {
      patchFields({ targetDurationSeconds: Math.round(n), durationSource: 'USER_CONFIRMED_VALUE' })
    }
    setCustomDurationDraft('')
  }
  const isCustomDurationActive =
    durationSource === 'USER_CONFIRMED_VALUE' && targetDurationSeconds != null && !DURATION_CHIPS.some((c) => c.seconds === targetDurationSeconds)

  const openAssetPicker = () => {
    nodeData.onOpenAssetPicker?.({
      nodeId: id,
      purpose: 'generic',
      title: '添加关联资产',
      selectionMode: 'multiple',
      maxSelection: 20,
      initialSelection: assets.map((a) => a.picker_item_id || `sa:${a.asset_id}`),
      onConfirm: (selections: ImageAssetPickerSelection[]) => {
        const next: AssetRef[] = selections.map((s) => ({
          asset_id: s.assetId,
          picker_item_id: s.pickerItemId,
          asset_type: 'image_asset',
          display_name: s.filename || s.assetId,
        }))
        patchFields({ assets: next })
      },
    })
  }
  const removeAsset = (assetId: string) => {
    patchFields({ assets: assets.filter((a) => a.asset_id !== assetId) })
  }

  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((opt) => ({
        value: opt.provider_option_id,
        disabled: !isProviderOptionSelectable(opt),
        label: opt.frontend_display_name,
        title: opt.frontend_description || opt.frontend_display_name,
      })),
    [providerOptions],
  )

  const runStatusLabel = useMemo(() => {
    if (runStatus === 'SUBMITTING') return '正在提交…'
    if (runStatus === 'QUEUED') return '排队中…'
    if (runStatus === 'GENERATING') return '正在构思创意方向…'
    if (runStatus === 'ERROR') return runErrorMessage || '生成失败'
    return null
  }, [runErrorMessage, runStatus])

  const canGenerate = userIntent.trim().length > 0 && !isBusy

  return (
    <div
      className={`pcv-node pcv-node--project-start${selected ? ' is-selected' : ''}`}
      style={{ width: NODE_WIDTH }}
      data-testid="canvas-project-start-node"
    >
      {/* Source-only handle (spec §33 "必须在 Canvas 建立明确 edges") — this
       * node never has anything connect INTO it via the dataflow system
       * (Brief edits happen through the node's own form, not upstream
       * nodes), it only ever fans OUT into `creative_candidate` nodes, so
       * no matching `target` Handle is rendered here. Default (no `id`)
       * to match the default (no `id`) `target` Handle on
       * `CreativeCandidateNode` — the `GENERATED_FROM` edges created in
       * `CanvasBoard.materializeCreativeCandidates` never set an explicit
       * `sourceHandle`/`targetHandle`. */}
      <Handle type="source" position={Position.Right} className="pcv-handle" />
      <div className="pcv-brief-header">
        <span className="pcv-brief-header-icon" style={{ color: def.color, background: `${def.color}22` }}>
          {def.icon}
        </span>
        <span className="pcv-brief-header-title">{def.label}</span>
        <span className="pcv-brief-header-badge">起点节点</span>
      </div>
      <div className="pcv-brief-subtitle">用一个想法，开启你的创意之旅</div>

      <div className="pcv-brief-body">
        {/* §5 创作意图 — the only truly required field */}
        <div className="pcv-brief-section">
          <span className="pcv-brief-section-label">创作意图（必填）</span>
          <textarea
            className="pcv-brief-textarea nodrag"
            placeholder="告诉我你想做什么，可以很简单……"
            value={userIntent}
            maxLength={INTENT_MAX_LENGTH}
            onChange={(e) => patchFields({ userIntent: e.target.value })}
          />
          <span className="pcv-brief-textarea-counter">
            {userIntent.length}/{INTENT_MAX_LENGTH}
          </span>
          <div className="pcv-brief-chip-row">
            {INTENT_EXAMPLES.map((ex) => (
              <span key={ex} className="pcv-brief-chip nodrag" onClick={() => appendExample(ex)}>
                {ex}
              </span>
            ))}
          </div>
        </div>

        {/* §7 内容类型 */}
        <div className="pcv-brief-section">
          <span className="pcv-brief-section-label">
            内容类型 <span className="pcv-brief-section-optional">（可选，AI 判断）</span>
          </span>
          <CanvasSelect
            size="small"
            className="pcv-gen-field-select nodrag"
            popupClassName="pcv-gen-field-select-popup"
            value={contentGoal ?? undefined}
            placeholder="AI 判断"
            allowClear
            options={CONTENT_GOAL_OPTIONS.map((label) => ({ value: label, label }))}
            onChange={(v) => patchFields({ contentGoal: v ?? null, contentGoalSource: v ? 'USER_CONFIRMED' : 'UNKNOWN' })}
            onClear={() => patchFields({ contentGoal: null, contentGoalSource: 'UNKNOWN' })}
            popupMatchSelectWidth={false}
            placement="bottomLeft"
            getPopupContainer={() => document.body}
          />
        </div>

        {/* §8 视频时长 */}
        <div className="pcv-brief-section">
          <span className="pcv-brief-section-label">
            视频时长 <span className="pcv-brief-section-optional">（可选）</span>
          </span>
          <div className="pcv-brief-chip-row nodrag">
            {DURATION_CHIPS.map((chip) => (
              <span
                key={chip.key}
                className={`pcv-brief-chip${targetDurationSeconds === chip.seconds && durationSource !== 'AI_SUGGESTED' ? ' is-active' : ''}`}
                onClick={() => pickDuration(chip)}
              >
                {chip.label}
              </span>
            ))}
            <span className={`pcv-brief-chip${isCustomDurationActive ? ' is-active' : ''}`}>
              <input
                style={{ width: 40, border: 'none', background: 'none', color: 'inherit', outline: 'none', fontSize: 11 }}
                placeholder="自定义"
                value={customDurationDraft}
                onChange={(e) => setCustomDurationDraft(e.target.value.replace(/[^0-9]/g, ''))}
                onBlur={commitCustomDuration}
                onKeyDown={(e) => e.key === 'Enter' && commitCustomDuration()}
              />
              秒
            </span>
            <span className={`pcv-brief-chip${durationSource === 'AI_SUGGESTED' ? ' is-active' : ''}`} onClick={pickAiSuggestedDuration}>
              AI 建议
            </span>
          </div>
        </div>

        {/* §11-§13 已关联资产 */}
        <div className="pcv-brief-section">
          <span className="pcv-brief-section-label">
            已关联资产 <span className="pcv-brief-section-optional">（可选，没有也能开始）</span>
          </span>
          <div className="pcv-brief-asset-row">
            {assets.map((a) => (
              <AssetChip key={a.asset_id} asset={a} workspaceId={workspaceId} onRemove={() => removeAsset(a.asset_id)} />
            ))}
            <button type="button" className="pcv-brief-asset-add-btn nodrag" onClick={openAssetPicker}>
              <PlusOutlined /> 添加资产
            </button>
          </div>
          {assets.length === 0 ? <span className="pcv-brief-asset-empty">暂无关联资产</span> : null}
        </div>

        {/* §16-§18 创意方式 / Skill */}
        <div className="pcv-gen-field-row">
          <span className="pcv-gen-field-label">创意方式 / Skill</span>
          <CanvasSelect
            size="small"
            className="pcv-gen-field-select nodrag"
            popupClassName="pcv-gen-field-select-popup"
            value={skillSelectionSource === 'EXPLICIT' ? 'creative_ideation' : 'AUTO'}
            options={[
              { value: 'AUTO', label: '自动（推荐）' },
              { value: 'creative_ideation', label: '创意方向生成' },
            ]}
            onChange={(v) =>
              patchFields(
                v === 'AUTO'
                  ? { skillSelectionSource: 'AUTO', skillKey: null }
                  : { skillSelectionSource: 'EXPLICIT', skillKey: v },
              )
            }
            popupMatchSelectWidth={false}
            placement="bottomLeft"
            getPopupContainer={() => document.body}
          />
        </div>

        {/* §19-§24 Provider */}
        <div className="pcv-gen-field-row" data-testid="ideation-provider-row">
          <span className="pcv-gen-field-label">Provider</span>
          <CanvasSelect
            size="small"
            className="pcv-gen-field-select nodrag"
            popupClassName="pcv-gen-field-select-popup"
            data-testid="ideation-provider-select"
            value={resolvedProviderOptionId ?? undefined}
            disabled={providerOptionsError || providerSelectOptions.length === 0}
            placeholder={providerOptionsError ? '加载失败' : '选择 Provider'}
            options={providerSelectOptions}
            onChange={(v) => patchFields(buildExplicitProviderSelectionPatch(v))}
            popupMatchSelectWidth={false}
            placement="bottomLeft"
            getPopupContainer={() => document.body}
          />
        </div>

        {/* §44-§46 更多创作要求（折叠） */}
        <button
          type="button"
          className="pcv-brief-collapse-toggle nodrag"
          onClick={() => {
            const next = !moreOpen
            setMoreOpen(next)
            patchFields({ moreRequirementsExpanded: next })
          }}
        >
          {moreOpen ? <DownOutlined /> : <RightOutlined />} 更多创作要求（可选）
        </button>
        {moreOpen ? (
          <div className="pcv-brief-more-body">
            <div className="pcv-brief-section">
              <span className="pcv-brief-section-label">必须出现</span>
              <TagListEditor values={mustInclude} placeholder="输入后按 Enter 添加" onChange={(v) => patchFields({ mustInclude: v })} />
            </div>
            <div className="pcv-brief-section">
              <span className="pcv-brief-section-label">不能出现</span>
              <TagListEditor values={mustAvoid} placeholder="输入后按 Enter 添加" onChange={(v) => patchFields({ mustAvoid: v })} />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">目标受众</span>
              <input
                className="pcv-brief-textarea nodrag"
                style={{ minHeight: 30, height: 30, padding: '5px 8px' }}
                value={audience}
                placeholder="例如：25-40岁城市家庭"
                onChange={(e) => patchFields({ audience: e.target.value })}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">发布平台</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={platform ?? undefined}
                allowClear
                placeholder="不限"
                options={PLATFORM_OPTIONS.map((p) => ({ value: p, label: p }))}
                onChange={(v) => patchFields({ platform: v ?? null })}
                onClear={() => patchFields({ platform: null })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">风格 / Tone</span>
              <input
                className="pcv-brief-textarea nodrag"
                style={{ minHeight: 30, height: 30, padding: '5px 8px' }}
                value={tone}
                placeholder="例如：电影感、幽默、温暖"
                onChange={(e) => patchFields({ tone: e.target.value })}
              />
            </div>
            <div className="pcv-brief-section">
              <span className="pcv-brief-section-label">参考内容</span>
              <TagListEditor values={references} placeholder="输入内容/资产 ID 后按 Enter" onChange={(v) => patchFields({ references: v })} />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">生成候选数</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={candidateCount}
                options={CANDIDATE_COUNT_OPTIONS.map((n) => ({ value: n, label: `${n} 个方案` }))}
                onChange={(v) => patchFields({ candidateCount: v })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
          </div>
        ) : null}

        {/* §27-§29 生成创意方向 */}
        <div className="pcv-brief-generate-row">
          {runStatus === 'ERROR' && runErrorMessage ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{runErrorMessage}</div>
          ) : null}
          <button
            type="button"
            className="pcv-brief-generate-btn nodrag"
            disabled={!canGenerate}
            onClick={() => nodeData.onGenerateCreativeIdeation?.(id)}
          >
            {isBusy ? <LoadingOutlined spin /> : null} {isBusy ? '正在生成…' : '生成创意方向 →'}
          </button>
          {runStatusLabel && isBusy ? <span className="pcv-brief-run-status">{runStatusLabel}</span> : null}
          {!isBusy ? <span className="pcv-brief-generate-hint">基于你的输入，生成多个创意方向供选择</span> : null}
        </div>
      </div>
    </div>
  )
}

export default memo(ProjectStartNodeImpl)
